import { useState, useEffect } from 'react';
import './ApplicantCard.css';
import { supabase } from '../../lib/supabaseClient.js';

// 동아리 지원자 목록을 카드 형태로 보여주는 컴포넌트
export default function ApplicantCard({ clubId }) {
    // 지원자 목록 저장
    const [applicants, setApplicants] = useState([]);

    // 모달에 표시할 선택된 지원자 정보
    const [selectedApplicant, setSelectedApplicant] = useState(null);

    // 현재 로그인한 사용자의 동아리 내 역할 저장
    const [memberRole, setMemberRole] = useState(null);

   useEffect(() => {
        // 현재 사용자 역할 조회
        fetchUserAndRole();

        // 지원자 목록 조회
        fetchApplicants();

        // 지원자 목록을 2초마다 갱신
        const interval = setInterval(() => {
            fetchApplicants();
        }, 2000);

        // 컴포넌트 종료 시 반복 실행 해제
        return () => clearInterval(interval);
    }, [clubId]);

    // 현재 로그인한 사용자의 동아리 역할 조회
    async function fetchUserAndRole() {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // 로그인한 사용자의 users 테이블 id 조회
        const { data: userData } = await supabase
            .from('users')
            .select('id')
            .eq('email', user.email)
            .single();

        if (!userData) return;

        // 해당 동아리에서 사용자의 역할 조회
        const { data: memberData } = await supabase
            .from('club_members')
            .select('role')
            .eq('club_id', clubId)
            .eq('user_id', userData.id)
            .maybeSingle();

        setMemberRole(memberData?.role ?? null);
    }

    // 대기 상태인 지원자 목록 조회
    async function fetchApplicants() {
        const { data, error } = await supabase
            .from('club_applications')
            .select('*, clubs(name)')
            .eq('club_id', clubId)
            .eq('status', '대기');

        if (error) {
            console.error(error);
            return;
        }

        setApplicants(data ?? []);
    }

    // 현재 동아리 회원 수 조회
    async function fetchMemberCount() {
        const { data } = await supabase
            .from('clubs')
            .select('member_count')
            .eq('id', clubId)
            .single();

        return data?.member_count ?? 0;
    }

    // 지원자를 동아리원으로 승인
    async function approveApplicant(applicant) {
        const confirmed = window.confirm(`${applicant.name}님을 동아리원으로 승인하시겠습니까?`);
        if (!confirmed) return;

        // club_members 테이블에 동아리원 추가
        const { error: memberError } = await supabase
            .from('club_members')
            .insert([
                {
                    club_id: applicant.club_id,
                    user_id: applicant.user_id,
                    role: 'member',
                },
            ]);

        if (memberError) {
            alert('동아리원 추가 실패');
            return;
        }

        const count = await fetchMemberCount();

        // 동아리 회원 수 증가
        const { error: countError } = await supabase
            .from('clubs')
            .update({ member_count: count + 1 })
            .eq('id', applicant.club_id);

        if (countError) {
            alert('회원 수 증가 실패');
            return;
        }

        // 승인 완료 후 지원서 삭제
        const { error: deleteError } = await supabase
            .from('club_applications')
            .delete()
            .eq('id', applicant.id);

        if (deleteError) {
            alert('지원서 삭제 실패');
            return;
        }

        alert('승인 처리되었습니다.');
        setSelectedApplicant(null);
        fetchApplicants();
    }

    // 지원자 거절 처리
    async function rejectApplicant(applicant) {
        const confirmed = window.confirm(`${applicant.name}님의 지원을 거절하시겠습니까?`);
        if (!confirmed) return;

        // 거절된 지원서 삭제
        const { error } = await supabase
            .from('club_applications')
            .delete()
            .eq('id', applicant.id);

        if (error) {
            alert('거절 처리 실패');
            return;
        }

        alert('거절 처리되었습니다.');
        setSelectedApplicant(null);
        fetchApplicants();
    }

    // 지원자가 없을 때 안내 문구 표시
    if (applicants.length === 0) {
        return <div className="ac-empty">지원자가 없습니다.</div>;
    }

    return (
        <>

            {/* 지원자 카드 목록 */}
            <div className="ac-grid">
                {applicants.map((a) => (
                    <div
                        key={a.id}
                        className="ac-card"
                        onClick={() => setSelectedApplicant(a)}
                    >
                        <div className="ac-card-body">
                            <p className="ac-card-name">{a.name || '이름 없음'}</p>
                            <p className="ac-card-sub">{a.department || '-'}</p>
                            <p className="ac-card-sub">
                                {a.desired_activity?.trim() || '없음'}
                            </p>

                            <div className="ac-card-footer">
                                <span className="ac-tag">
                                    {a.grade ? `${a.grade}학년` : '-'}
                                </span>
                                <span className="ac-card-id">
                                    {a.student_number || '-'}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* 지원자 상세 정보 모달 */}
            {selectedApplicant && (
                <div className="ac-modal-bg">
                    <div className="ac-modal">
                        <button
                            className="ac-modal-close"
                            onClick={() => setSelectedApplicant(null)}
                        >
                            ×
                        </button>

                        <h2 className="ac-modal-name">
                            {selectedApplicant.name || '이름 없음'}
                        </h2>

                        {/* 지원자 상세 정보 */}
                        <div className="ac-modal-fields">
                            <div className="ac-modal-field">
                                <span className="ac-modal-label">지원 동아리</span>
                                <span className="ac-modal-value">
                                    {selectedApplicant.clubs?.name ?? '-'}
                                </span>
                            </div>

                            <div className="ac-modal-field">
                                <span className="ac-modal-label">전화번호</span>
                                <span className="ac-modal-value">
                                    {selectedApplicant.phone ?? '-'}
                                </span>
                            </div>

                            <div className="ac-modal-field">
                                <span className="ac-modal-label">학년</span>
                                <span className="ac-modal-value">
                                    {selectedApplicant.grade
                                        ? `${selectedApplicant.grade}학년`
                                        : '-'}
                                </span>
                            </div>

                            <div className="ac-modal-field">
                                <span className="ac-modal-label">학번</span>
                                <span className="ac-modal-value">
                                    {selectedApplicant.student_number ?? '-'}
                                </span>
                            </div>

                            <div className="ac-modal-field">
                                <span className="ac-modal-label">학과</span>
                                <span className="ac-modal-value">
                                    {selectedApplicant.department ?? '-'}
                                </span>
                            </div>

                            <div className="ac-modal-field">
                                <span className="ac-modal-label">성별</span>
                                <span className="ac-modal-value">
                                    {selectedApplicant.gender ?? '-'}
                                </span>
                            </div>

                            <div className="ac-modal-field column">
                                <span className="ac-modal-label">해보고 싶은 활동</span>
                                <span className="ac-modal-value">
                                    {selectedApplicant.desired_activity?.trim() || '없음'}
                                </span>
                            </div>
                        </div>

                        {/* 동아리장만 승인 및 거절 가능 */}
                        {memberRole === 'leader' && (
                            <div className="ac-modal-actions">
                                <button
                                    className="ac-action-btn expel"
                                    onClick={() => rejectApplicant(selectedApplicant)}
                                >
                                    거절
                                </button>
                                <button
                                    className="ac-action-btn approve"
                                    onClick={() => approveApplicant(selectedApplicant)}
                                >
                                    승인
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}